import { Eye, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { useAuthStore } from "@/store/auth";
import { impersonationContextAtom } from "@/store/impersonation";

interface Props {
  onExit: () => void;
}

/**
 * Prominent, sticky amber banner shown while an impersonation session is active
 * (TASK-3677, extended Sprint 139). Pinned to the top (`sticky top-0`, high
 * z-index) so it never scrolls away, names the impersonated target, and exposes
 * a clearly labeled Exit control. The exit logic is supplied by the caller
 * (AppShell / DealerShell / the "impersonating" blocked state).
 *
 * Renders one of three variants, discriminated by the persisted impersonation
 * context (Decision D5):
 *   - superadmin→tenant / dealer→tenant  → "Viewing as: <tenant>"
 *   - superadmin→dealer                  → "Viewing as dealer: <dealer>"
 *
 * The target label is read from the impersonation context (`displayName`) with
 * a fallback to the tenant name on the live profile for legacy tenant sessions
 * started before the context existed.
 */
export function ImpersonationBanner({ onExit }: Props) {
  const { t } = useTranslation();
  const context = useAtomValue(impersonationContextAtom);
  const tenantName = useAuthStore((s) => s.user?.tenant_name ?? "");

  const isDealerTarget = context?.targetKind === "dealer";
  const targetName = context?.displayName ?? tenantName;
  const label = isDealerTarget
    ? t("adminPanel.impersonationBannerDealer", { dealerName: targetName })
    : t("adminPanel.impersonationBanner", { tenantName: targetName });

  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-amber-500 bg-amber-400 px-4 py-2 shadow-sm"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Eye size={16} className="shrink-0 text-amber-900" />
        <span className="truncate text-sm font-semibold text-amber-900">
          {label}
        </span>
      </div>
      <button
        type="button"
        onClick={onExit}
        className="shrink-0 rounded-lg bg-amber-900 px-3 py-1 text-xs font-semibold text-amber-50 transition-colors hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-900 focus:ring-offset-1 focus:ring-offset-amber-400"
      >
        <span className="flex items-center gap-1.5">
          <LogOut size={12} />
          {t("adminPanel.exitImpersonation")}
        </span>
      </button>
    </div>
  );
}
